import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { object, string, ref } from "yup";
import { createUser, resetStatus } from "../redux/usersSagas";
import { useError } from "../hooks/useError";
import { MainModal } from "../../../../components/modal/MainModal";
import LoadingAnimation from "../../../../components/loadingAnimation/loadingAnimation";
import Dropdown from "../../../../components/multiSelectDropdown/multiSelectDropdown";
import ToggleableButton from "../../../../components/toggleableButton/toggleableButton";
import { Input, PasswordInput } from "../../../../components/form/Input/Input";
import { generatePassword, passwordSchemaTest } from "../../../../utils/password-utils";
import ReactDatePicker from "../../../../components/reactDatePicker/reactDatePicker";
import { ErrorList } from "../../../../components/ErrorList";
import { showToast } from "../../../../utils/showToast";

const schema = object({
  first_name: string().label("First Name").trim().min(2).required(),
  last_name: string().label("Last Name").trim().min(2).required(),
  email: string().label("Email").trim().email().required(),
  password: string().label("Password").min(6).max(40).test(passwordSchemaTest).required(),
  password_confirmation: string()
    .label("Password Confirmation")
    .oneOf([ref("password")], "${path} must match Password")
    .required(),
  organization_id: object().required("Please choose organization"),
  role_id: object().required("Please choose role")
}).required();

const generatedPassword = generatePassword();

export const Add = ({ onClose }) => {
  const dispatch = useDispatch();

  const {
    filters: { roles, organizations }
  } = useSelector((state) => state.users.commonSettings);

  const {
    register,
    watch,
    handleSubmit,
    control,
    setValue,
    formState: { errors, isSubmitting }
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      send_welcome_email: true,
      inactive_client: false,
      corporate: false,
      password_changed: false,
      tos_accepted: false,
      expires_at: null,
      password: generatedPassword,
      password_confirmation: generatedPassword
    }
  });

  const watchedOrg = watch("organization_id", {});

  const filterRolesList = () => {
    if (!watchedOrg.id) return [];
    return roles.filter((role) => role.organizationId === watchedOrg.id);
  };

  const orgRoles = filterRolesList();

  const close = () => {
    dispatch(resetStatus());
    onClose();
  };

  const { errors: serverErrors, status } = useError(() => {
    showToast("User was successfully created");
    close();
  });

  const primaryButtonDisabled = isSubmitting || status == "pending";

  const onSubmit = async (data) => {
    dispatch(createUser({ ...data, phone_number: data.phone_number?.trim() }));
  };

  useEffect(() => {
    if (watchedOrg.id) {
      setValue("role_id", null);
    }
  }, [watchedOrg]);

  return (
    <>
      <MainModal
        isOpen
        onClose={close}
        parentSelector=".admin-users"
        headerText="Add User"
        headerCloseHandler={close}
        bodyClassName="main-popup-body"
        primaryButtonText="Save"
        primaryButtonDisabled={primaryButtonDisabled}
        primaryBtnHandler={handleSubmit(onSubmit)}
        secondaryBtnText="Cancel"
        secondaryBtnHandler={close}
        height="auto"
        width={900}
      >
        <div>
          <div className="admin-users__section">
            <Input label="First Name" htmlFor="first_name" customProps={register("first_name")} />
            <Input label="Last Name" htmlFor="last_name" customProps={register("last_name")} />
            <Input label="Email" htmlFor="email" customProps={register("email")} />
          </div>

          <div className="admin-users__section">
            <PasswordInput label="Password" customProps={register("password")} htmlFor="password" />
            <PasswordInput
              label="Password Confirmation"
              customProps={register("password_confirmation")}
              htmlFor="password_confirmation"
            />

            <div className="admin-users__date">
              <p>Expires at</p>

              <Controller
                control={control}
                name="expires_at"
                render={({ field: { onChange, onBlur, value } }) => {
                  return (
                    <ReactDatePicker
                      showDefaultInputIcon={false}
                      showClearDate
                      onBlur={onBlur}
                      startDate={value}
                      allowAllDays
                      updateDates={(data) => onChange(data)}
                    />
                  );
                }}
              />
            </div>
          </div>

          <div className="admin-users__section">
            <Input
              label="Phone number"
              htmlFor="phone_number"
              placeholder="+1 (999) 999 9999"
              customProps={register("phone_number")}
            />
            <div className="admin-users__dropdown">
              <p>Organization</p>
              <Controller
                control={control}
                name="organization_id"
                render={({ field: { onChange, onBlur, value } }) => {
                  return (
                    <Dropdown
                      onBlur={onBlur}
                      height={275}
                      haveOnSelectChange
                      options={organizations}
                      selectedFilters={value}
                      numberDisplayed={0}
                      onSelectChange={(data) => onChange(data)}
                    />
                  );
                }}
              />
            </div>
            {watchedOrg.id ? (
              <div className="admin-users__dropdown">
                <p>Roles</p>
                <Controller
                  control={control}
                  name="role_id"
                  render={({ field: { onChange, onBlur, value } }) => {
                    return (
                      <Dropdown
                        onBlur={onBlur}
                        height={275}
                        haveOnSelectChange
                        options={orgRoles}
                        selectedFilters={value}
                        numberDisplayed={0}
                        onSelectChange={(data) => onChange(data)}
                      />
                    );
                  }}
                />
              </div>
            ) : (
              <div className="admin-users__empty" />
            )}
          </div>
          <div className="admin-users__section">
            <div className="switch">
              <p className="switch__title">Send Welcome Email</p>
              <Controller
                control={control}
                name="send_welcome_email"
                render={({ field: { onChange, value } }) => {
                  return (
                    <div className={`switch__content ${value ? "switch__content--active" : ""}`}>
                      <ToggleableButton
                        isRadio
                        title="showWelcomeEmail"
                        isActive={value}
                        onClick={(isActive) => onChange(isActive)}
                      />
                    </div>
                  );
                }}
              />
            </div>
            <div className="switch">
              <p className="switch__title">Inactive Client</p>
              <Controller
                control={control}
                name="inactive_client"
                render={({ field: { onChange, value } }) => {
                  return (
                    <div className={`switch__content ${value ? "switch__content--active" : ""}`}>
                      <ToggleableButton
                        isRadio
                        title="inactiveClient"
                        isActive={value}
                        onClick={(isActive) => onChange(isActive)}
                      />
                    </div>
                  );
                }}
              />
            </div>
            <div className="switch">
              <p className="switch__title">Corporate</p>
              <Controller
                control={control}
                name="corporate"
                render={({ field: { onChange, value } }) => {
                  return (
                    <div className={`switch__content ${value ? "switch__content--active" : ""}`}>
                      <ToggleableButton
                        isRadio
                        title="corporate"
                        isActive={value}
                        onClick={(isActive) => onChange(isActive)}
                      />
                    </div>
                  );
                }}
              />
            </div>
            <div className="switch">
              <p className="switch__title">Password Changed</p>
              <Controller
                control={control}
                name="password_changed"
                render={({ field: { onChange, value } }) => {
                  return (
                    <div className={`switch__content ${value ? "switch__content--active" : ""}`}>
                      <ToggleableButton
                        isRadio
                        title="passwordChanged"
                        isActive={value}
                        onClick={(isActive) => onChange(isActive)}
                      />
                    </div>
                  );
                }}
              />
            </div>
            <div className="switch">
              <p className="switch__title">TOS Accepted</p>
              <Controller
                control={control}
                name="tos_accepted"
                render={({ field: { onChange, value } }) => {
                  return (
                    <div className={`switch__content ${value ? "switch__content--active" : ""}`}>
                      <ToggleableButton
                        isRadio
                        title="tosAccepted"
                        isActive={value}
                        onClick={(isActive) => onChange(isActive)}
                      />
                    </div>
                  );
                }}
              />
            </div>
          </div>

          {status === "pending" && <LoadingAnimation uiLoader />}
          <ErrorList errors={{ ...errors, ...serverErrors }} />
        </div>
      </MainModal>
    </>
  );
};
